import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  REVIEW_DECISION,
  type ReviewDecision,
  type ReviewPayload,
} from '@/components/common/ReviewDecision';
import { teacherSubmissionQueryKeys } from '@/features/teacherSubmissions/api/teacherSubmissions.api';
import { showApiErrorToast } from '@/utils/errors';
import {
  approveTeacherInactivation,
  rejectTeacherInactivation,
  teacherInactivationQueryKeys,
} from '../api/teacherInactivations.api';
import type { TeacherInactivationDetail } from '../types/teacherInactivation.types';

interface ReviewArgs {
  id: number;
  decision: ReviewDecision;
  payload: ReviewPayload;
}

const REVIEW_MESSAGE: Record<ReviewDecision, { success: string; failure: string }> = {
  APPROVE: {
    success: 'Request approved. The teacher is now inactive.',
    failure: 'Failed to approve the request',
  },
  REJECT: {
    success: 'Request rejected. The teacher stays active.',
    failure: 'Failed to reject the request',
  },
};

export function useReviewTeacherInactivation() {
  const queryClient = useQueryClient();

  function refreshRequests(id: number) {
    void queryClient.invalidateQueries({ queryKey: teacherInactivationQueryKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: teacherInactivationQueryKeys.summaries() });
    void queryClient.invalidateQueries({ queryKey: teacherInactivationQueryKeys.detail(id) });
  }

  return useMutation({
    mutationFn: ({ id, decision, payload }: ReviewArgs) =>
      decision === REVIEW_DECISION.APPROVE
        ? approveTeacherInactivation(id, payload)
        : rejectTeacherInactivation(id, payload),
    onSuccess: (request, { id, decision }) => {
      queryClient.setQueryData<TeacherInactivationDetail>(
        teacherInactivationQueryKeys.detail(id),
        (current) => current && { ...current, ...request },
      );
      refreshRequests(id);
      if (decision === REVIEW_DECISION.APPROVE) {
        void queryClient.invalidateQueries({ queryKey: teacherSubmissionQueryKeys.all() });
      }
      toast.success(REVIEW_MESSAGE[decision].success);
    },
    onError: (error, { id, decision }) => {
      refreshRequests(id);
      showApiErrorToast(error, REVIEW_MESSAGE[decision].failure);
    },
  });
}
